import React, { FC, useState, useEffect } from 'react';
import { useMutation } from 'react-apollo';

import UPDATE_USER_THEME_PREFERENCE_MUTATION from './graphql/UpdateUserThemePreference.graphql';
import {
  UpdateUserThemePreference as UpdateUserThemePreferenceResponse,
  UpdateUserThemePreferenceVariables,
} from './graphql/types/UpdateUserThemePreference';

import customLocalStorage from 'Jsx/Utils/customLocalStorage';
import { useMe } from 'Jsx/ContextApis/AuthContext';

import { UITheme } from './types';
import UIThemeContext from './UIThemeContext';

const UIThemeProvider: FC = ({ children }) => {
  const initialTheme = customLocalStorage.getItem('crehana-dark-mode')
    ? 'dark'
    : 'light';

  const [{ theme, isDark }, setState] = useState<{
    theme: UITheme;
    isDark: boolean;
  }>({ theme: initialTheme, isDark: initialTheme === 'dark' });

  const { me } = useMe();

  const [updateUserThemePreferenceMutation] = useMutation<
    UpdateUserThemePreferenceResponse,
    UpdateUserThemePreferenceVariables
  >(UPDATE_USER_THEME_PREFERENCE_MUTATION);

  useEffect(() => {
    if (me?.settings) {
      const { darkMode } = me.settings;

      setState({
        theme: darkMode ? 'dark' : 'light',
        isDark: !!darkMode,
      });
    }
  }, [me]);

  useEffect(() => {
    if (isDark) {
      customLocalStorage.setItem('crehana-dark-mode', 'true');
    } else {
      customLocalStorage.removeItem('crehana-dark-mode');
    }
  }, [isDark]);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    const darkMode = newTheme === 'dark';

    setState({ theme: newTheme, isDark: darkMode });

    updateUserThemePreferenceMutation({ variables: { darkMode } });

    if (darkMode) {
      window.track('Switch to dark mode');
    }
  };

  return (
    <UIThemeContext.Provider value={{ theme, isDark, toggleTheme }}>
      {children}
    </UIThemeContext.Provider>
  );
};

export default UIThemeProvider;
